<template>
  <div class="custom-container">
    <div class="ms-cell-editor combobox" :class="{'cell-error': errorMessage}">
      <div
        v-show="!isSelected || readOnly"
        :class="{
          'edit-disabled': readOnly,
          'ms-editor-text': editable,
          hasError: errorProvider.isValid,
          'display-error': errorMessage && !isEditor && displayError,
        }"
      >
        <a v-show="!isEditor" @click="$emit('editorClick', $event, col, colIndex)" class="editor-click">
          <span
            class="editor-display"
            :title="[
              dataRow[col.dataField]
                ? $ms.format.formatData(dataRow[col.dataField], {
                    formatType: col.formatType,
                    enumName: col.enum,
                  })
                : '',
            ]"
            >{{
              dataRow[col.dataField]
                ? $ms.format.formatData(dataRow[col.dataField], {
                    formatType:
                      customControlType && dataRow._customType ? dataRow._customType[col.dataField] : col.formatType,
                    enumName: col.enum,
                  })
                : ''
            }}</span
          >
        </a>

        <ms-combobox
          v-if="isEditor"
          :data="comboData"
          :columns="propsData.columns"
          :suffixDisplayField="propsData.suffixDisplayField"
          :displayField="propsData.displayField"
          :valueField="propsData.valueField"
          v-model="dataRow[modelValueField]"
          v-model:display="dataRow[col.dataField]"
          v-model:suffixDisplay="dataRow[propsData.suffixDisplayField]"
          :initText="dataRow[col.dataField]"
          :quickAdd="offlineReady ? null : propsData.quickAdd"
          :requestField="propsData.requestField"
          :selectOnly="propsData.selectOnly"
          :setItem="propsData.setItem"
          :prioritiFieldName="propsData.prioritiFieldName"
          :quickAddMulti="offlineReady ? null : propsData.quickAddMulti"
          :validateQuickAdd="propsData.validateQuickAdd"
          :getModelData="propsData.getModelData"
          :quickSearch="propsData.quickSearch"
          :quickAddObject="propsData.quickAddObject"
          :queryMode="queryModeClone || 'local'"
          :remoteFilter="queryModeClone === 'remote' ? true : false"
          v-on="listeners"
          :forceReload="propsData.forceReload"
          :dropdownWidth="propsData.dropdownWidth || 0"
          :quickSearchParam="propsData.quickSearchParam"
          :dataRow="dataRow"
          :col="col"
          :isGridEditor="true"
          :rules="propsData.rules || [{name: $ms.constant.ValidateRule.ForceSelection}]"
          ref="msCombobox"
          @selectItem="selectItem"
          @keydown.f7.prevent="e => eventShortkey(e, col, colIndex)"
          :preventPaste="allowCopyFromExcel"
          @callbackAddData="callbackAddData"
          :maxLength="propsData.maxLength"
        ></ms-combobox>

        <div class="icon24 error" v-tooltip="errorMessage" v-show="errorMessage && !isEditor && displayError"></div>
      </div>
      <div class="ms-editor control-cell" ref="editor" v-show="isSelected && !readOnly"></div>
    </div>
    <v-menu
      ref="optionMenu"
      class="option-wrapper"
      v-if="isEditor && isCopyDataColumn"
      popperClass="option-menu"
      :placement="isAtLastColumn ? 'bottom-start' : 'bottom-end'"
      :skidding="marginSetting"
      :width="menuContentWidth"
      :style="[{right: isAtLastColumn ? 'auto' : optionMenuRight}, {left: isAtLastColumn ? optionMenuLeft : 'auto'}]"
      @show="handleMenuShow"
      :class="dbOptionHeight"
    >
      <div :class="['icon24', 'option-tab-vertical', 'option-custom']"></div>
      <template #popper>
        <menu-item
          v-for="(item, i) in editorActions"
          :key="i"
          @menu-item-click="clickMenuItem(item.id, rowIndex, colIndex)"
          >{{ item.text }}</menu-item
        >
      </template>
    </v-menu>
  </div>
</template>

<script setup></script>
<script>
import {computed, watch, nextTick, ref, getCurrentInstance, inject} from 'vue';
import {useCellEditor} from './CellEditorSetup.js';
import MenuItem from '@/components/dropdown/MenuItem.vue';
import {ModuleOfflineWork} from '@/stores/moduleConst';
export default {
  name: 'MsCellCombobox',
  components: {
    MenuItem,
  },
  props: {
    col: {},
    dataRow: {},
    rowIndex: {
      default: 0,
      type: Number,
    },
    colIndex: {
      default: 0,
      type: Number,
    },
    //Thuộc tính để cấu hình cho phép sửa trên grid hay không
    editable: {
      default: true,
      type: Boolean,
    },
    rowSelected: {
      default: null,
    },
    readOnly: {
      default: false,
      type: Boolean,
    },
    //có cho phép show text validate bên dưới cell k
    isShowText: {
      type: Boolean,
      default: false,
    },
    propsData: {
      type: Object,
      default: () => {},
    },
    rules: {
      type: Array,
      default: () => [],
    },
    disabledValidate: {
      default: false,
      type: Boolean,
    },
    // Hiện thị số dòng cho phép.
    lineBreak: {
      default: 1,
      type: Number,
    },
    /**
     * Cấu hình sao chép dữ liệu 1 cột xuống các cột khác (mục đích để dev phải check lại các màn hình đã làm để tránh lỗi dữ liệu)
     * dtnga3 (26.05.2023)
     */
    isCopyDataColumn: {
      default: false,
      type: Boolean,
    },
    optionMenuRight: {
      default: null,
      type: String,
    },
    optionMenuLeft: {
      default: null,
      type: String,
    },
    // Đánh dấu control nằm ở cột cuối hay không
    // dtnga3
    isAtLastColumn: {
      default: false,
      type: Boolean,
    },
    // Độ rộng menu content (cần set để tính toán vị trí của nó)
    // dtnga3
    menuContentWidth: {
      default: 194,
      type: Number,
    },
    customControlType: {
      default: false,
      type: Boolean,
    },
  },
  emits: [
    'editorClick',
    'comboboxLoadData',
    'msKeydown',
    'eventShortkeyAction',
    'comboBoxSelectItem',
    'copyActionClick',
    'pasteInput',
    'update:modelValue',
  ],
  setup(props, {emit}) {
    const {proxy} = getCurrentInstance();
    const comboData = computed(() => {
      if (props.propsData.queryMode === 'remote' && !proxy.offlineReady) {
        return props.propsData.data[props.col.dataField];
      }
      if (proxy.offlineReady && typeof props.propsData.getDataComboOffline == 'function') {
        return props.propsData.getDataComboOffline();
      }
      return props.propsData.data;
    });
    const {
      isEditor,
      errorProvider,
      isSelected,
      listeners,
      activeCell,
      inactiveCell,
      findEditor,
      errorMessage,
      validate,
      clearValidate,
      displayError,
      editorActions,
      clickMenuItem,
      marginSetting,
      handleMenuShow,
    } = useCellEditor({
      props,
      emit,
    });

    watch(isEditor, newVal => {
      if (newVal) {
        nextTick(() => {
          findEditor();
        });
      }
    });

    const allowCopyFromExcel = inject('allowCopyFromExcel');

    const modelValueField = props.propsData.modelValueField || props.propsData.valueField;
    const msCombobox = ref({});

    const getMetaData = () => {
      return msCombobox.value?.internalSelectedItem ? {...msCombobox.value.internalSelectedItem} : {};
    };

    /**
     * Click vào cell sẽ thực hiện update luôn do cell checkbox sẽ ko mở ra nhập liệu như cell input
     * PQDat 21/07/2022
     */
    const selectItem = metaData => {
      emit('comboBoxSelectItem', metaData);
    };

    /**
     * Xử lý shortkey action
     * HQMINH 22.08.2022
     */
    const eventShortkey = (e, col, colIndex) => {
      emit('eventShortkeyAction', e, col, colIndex);
    };

    const getValue = () => {
      return props.dataRow[props.col.dataField];
    };

    /**
     * Gán giá trị cho cell
     */
    const setValue = value => {
      const me = proxy;

      if (me.readOnly) {
        return;
      }

      if (isEditor.value && typeof me.$refs.msCombobox?.setInternalText == 'function') {
        me.$refs.msCombobox.setInternalText(value);
      }

      let find = me.comboData.find(x => x[me.propsData.displayField || me.col.dataField]?.trim() == value?.trim());
      if (find) {
        me.dataRow[me.col.dataField] = value;
        me.dataRow[modelValueField] = find[me.propsData.valueField || modelValueField];
      } else {
        me.dataRow[me.col.dataField] = value;
        me.dataRow[modelValueField] = null;
      }
      me.$emit('update:modelValue', me.dataRow[me.col.dataField]);

      return find;
    };

    const dbOptionHeight = computed(() => {
      return (
        'heightoptionvalue' +
        parseInt(
          proxy.$store?.state?.dbOption?.dbOptions.HeightOption == 1
            ? proxy.$store?.state?.dbOption?.dbOptions.HeightOption
            : 0,
        )
      );
    });

    const offlineReady = computed(() => {
      if (!proxy.$store.state[ModuleOfflineWork]) {
        return false;
      } else {
        return proxy.$store.state[ModuleOfflineWork].offlineReady;
      }
    });
    const queryModeClone = computed(() => {
      if (proxy.offlineReady) return 'local';
      return props.propsData.queryMode;
    });

    const callbackAddData = () => {
      proxy.$emit('callbackAddData', proxy.dataRow);
    };

    return {
      queryModeClone,
      offlineReady,
      isEditor,
      errorProvider,
      isSelected,
      listeners,
      activeCell,
      inactiveCell,
      findEditor,
      getValue,
      errorMessage,
      validate,
      clearValidate,
      modelValueField,
      comboData,
      getMetaData,
      msCombobox,
      eventShortkey,
      selectItem,
      displayError,
      editorActions,
      clickMenuItem,
      marginSetting,
      handleMenuShow,
      dbOptionHeight,
      setValue,
      allowCopyFromExcel,
      callbackAddData,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/components/CellAction.scss';
</style>
